/**
 * Design Token Editor
 * UI for managing design tokens (colors, typography, spacing, shadows, borders)
 */

import React, { useState, useCallback } from 'react';
import { Plus, Trash2, Save, Palette, Type, Maximize2, Sun, Square } from 'lucide-react';
import type { DesignToken, DesignTokenGroup } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface DesignTokenEditorProps {
  tokens: DesignToken[];
  siteId: string;
  onChange: (tokens: DesignToken[]) => void;
  onSave?: (token: DesignToken) => Promise<void>;
  onDelete?: (tokenId: string) => Promise<void>;
}

const TOKEN_GROUPS: { value: DesignTokenGroup; label: string; icon: React.ReactNode }[] = [
  { value: 'color', label: 'Colors', icon: <Palette className="h-4 w-4" /> },
  { value: 'typography', label: 'Typography', icon: <Type className="h-4 w-4" /> },
  { value: 'spacing', label: 'Spacing', icon: <Maximize2 className="h-4 w-4" /> },
  { value: 'shadow', label: 'Shadows', icon: <Sun className="h-4 w-4" /> },
  { value: 'border', label: 'Borders', icon: <Square className="h-4 w-4" /> },
];

// Default token presets
const TOKEN_PRESETS: Record<DesignTokenGroup, { name: string; value: string; label: string }[]> = {
  color: [
    { name: 'primary', value: '#3b82f6', label: 'Primary' },
    { name: 'secondary', value: '#6366f1', label: 'Secondary' },
    { name: 'accent', value: '#f59e0b', label: 'Accent' },
    { name: 'success', value: '#22c55e', label: 'Success' },
    { name: 'warning', value: '#eab308', label: 'Warning' },
    { name: 'error', value: '#ef4444', label: 'Error' },
    { name: 'text-primary', value: '#111827', label: 'Text Primary' },
    { name: 'text-secondary', value: '#6b7280', label: 'Text Secondary' },
    { name: 'bg-primary', value: '#ffffff', label: 'Background' },
    { name: 'bg-secondary', value: '#f9fafb', label: 'Background Alt' },
  ],
  typography: [
    { name: 'font-display', value: 'Inter, system-ui, sans-serif', label: 'Display Font' },
    { name: 'font-body', value: 'Inter, system-ui, sans-serif', label: 'Body Font' },
    { name: 'font-mono', value: 'JetBrains Mono, monospace', label: 'Mono Font' },
    { name: 'text-xs', value: '0.75rem', label: 'Extra Small' },
    { name: 'text-sm', value: '0.875rem', label: 'Small' },
    { name: 'text-base', value: '1rem', label: 'Base' },
    { name: 'text-lg', value: '1.125rem', label: 'Large' },
    { name: 'text-xl', value: '1.25rem', label: 'XL' },
    { name: 'text-2xl', value: '1.5rem', label: '2XL' },
    { name: 'text-3xl', value: '1.875rem', label: '3XL' },
  ],
  spacing: [
    { name: 'xs', value: '0.25rem', label: 'Extra Small' },
    { name: 'sm', value: '0.5rem', label: 'Small' },
    { name: 'md', value: '1rem', label: 'Medium' },
    { name: 'lg', value: '1.5rem', label: 'Large' },
    { name: 'xl', value: '2rem', label: 'Extra Large' },
    { name: '2xl', value: '3rem', label: '2XL' },
    { name: '3xl', value: '4rem', label: '3XL' },
  ],
  shadow: [
    { name: 'sm', value: '0 1px 2px 0 rgb(0 0 0 / 0.05)', label: 'Small' },
    { name: 'md', value: '0 4px 6px -1px rgb(0 0 0 / 0.1)', label: 'Medium' },
    { name: 'lg', value: '0 10px 15px -3px rgb(0 0 0 / 0.1)', label: 'Large' },
    { name: 'xl', value: '0 20px 25px -5px rgb(0 0 0 / 0.1)', label: 'Extra Large' },
  ],
  border: [
    { name: 'radius-sm', value: '0.25rem', label: 'Small Radius' },
    { name: 'radius-md', value: '0.375rem', label: 'Medium Radius' },
    { name: 'radius-lg', value: '0.5rem', label: 'Large Radius' },
    { name: 'radius-xl', value: '0.75rem', label: 'XL Radius' },
    { name: 'radius-full', value: '9999px', label: 'Full Radius' },
    { name: 'width-thin', value: '1px', label: 'Thin Border' },
    { name: 'width-medium', value: '2px', label: 'Medium Border' },
  ],
};

// ============================================================================
// TOKEN ROW
// ============================================================================

interface TokenRowProps {
  token: DesignToken;
  group: DesignTokenGroup;
  onUpdate: (updates: Partial<DesignToken>) => void;
  onDelete: () => void;
}

function TokenRow({ token, group, onUpdate, onDelete }: TokenRowProps) {
  return (
    <div className="flex items-center gap-3 py-2 group">
      {/* Visual preview */}
      {group === 'color' && (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={token.tokenValue}
            onChange={(e) => onUpdate({ tokenValue: e.target.value })}
            className="h-8 w-8 rounded border cursor-pointer flex-shrink-0"
          />
        </div>
      )}
      {group === 'shadow' && (
        <div
          className="h-8 w-8 bg-white rounded border flex-shrink-0"
          style={{ boxShadow: token.tokenValue }}
        />
      )}
      {group === 'border' && token.tokenName.includes('radius') && (
        <div
          className="h-8 w-8 bg-blue-100 border-2 border-blue-400 flex-shrink-0"
          style={{ borderRadius: token.tokenValue }}
        />
      )}
      {group === 'spacing' && (
        <div className="h-8 flex items-center flex-shrink-0">
          <div
            className="h-4 bg-blue-200 rounded"
            style={{ width: token.tokenValue, minWidth: '4px', maxWidth: '64px' }}
          />
        </div>
      )}
      {group === 'typography' && (
        <span className="text-xs text-gray-400 w-8 flex-shrink-0 font-mono">Aa</span>
      )}

      {/* Label */}
      <input
        type="text"
        value={token.tokenLabel || ''}
        onChange={(e) => onUpdate({ tokenLabel: e.target.value })}
        placeholder="Label"
        className="w-28 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-0.5 text-sm"
      />

      {/* Name */}
      <input
        type="text"
        value={token.tokenName}
        onChange={(e) => onUpdate({ tokenName: e.target.value })}
        placeholder="token-name"
        className="w-28 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-0.5 text-sm font-mono text-gray-500"
      />

      {/* Value */}
      <input
        type="text"
        value={token.tokenValue}
        onChange={(e) => onUpdate({ tokenValue: e.target.value })}
        placeholder="value"
        className="flex-1 border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-0.5 text-sm font-mono"
      />

      {/* Delete */}
      <button
        onClick={onDelete}
        className="p-1 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="h-3.5 w-3.5 text-red-500" />
      </button>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function DesignTokenEditor({
  tokens,
  siteId,
  onChange,
  onSave,
  onDelete,
}: DesignTokenEditorProps) {
  const [activeGroup, setActiveGroup] = useState<DesignTokenGroup>('color');

  const groupTokens = tokens.filter(t => t.tokenGroup === activeGroup);

  const addToken = () => {
    const newToken: DesignToken = {
      id: `temp_${Date.now()}`,
      siteId,
      tokenGroup: activeGroup,
      tokenName: '',
      tokenValue: activeGroup === 'color' ? '#000000' : '',
      tokenLabel: '',
      sortOrder: groupTokens.length,
    };
    onChange([...tokens, newToken]);
  };

  const loadPresets = () => {
    const presets = TOKEN_PRESETS[activeGroup];
    const existingNames = new Set(groupTokens.map(t => t.tokenName));

    const newTokens = presets
      .filter(p => !existingNames.has(p.name))
      .map((p, i) => ({
        id: `temp_${Date.now()}_${i}`,
        siteId,
        tokenGroup: activeGroup,
        tokenName: p.name,
        tokenValue: p.value,
        tokenLabel: p.label,
        sortOrder: groupTokens.length + i,
      }));

    if (newTokens.length > 0) {
      onChange([...tokens, ...newTokens]);
    }
  };

  const updateToken = (tokenId: string, updates: Partial<DesignToken>) => {
    onChange(tokens.map(t => t.id === tokenId ? { ...t, ...updates } : t));
  };

  const deleteToken = (tokenId: string) => {
    onChange(tokens.filter(t => t.id !== tokenId));
    onDelete?.(tokenId);
  };

  // Generate CSS variables output
  const cssVariables = groupTokens
    .filter(t => t.tokenName)
    .map(t => `  --${activeGroup}-${t.tokenName}: ${t.tokenValue};`)
    .join('\n');

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Palette className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Design Tokens</h2>
        </div>
      </div>

      {/* Group Tabs */}
      <div className="border-b px-4 flex gap-1">
        {TOKEN_GROUPS.map((group) => {
          const count = tokens.filter(t => t.tokenGroup === group.value).length;
          return (
            <button
              key={group.value}
              onClick={() => setActiveGroup(group.value)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
                activeGroup === group.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {group.icon}
              {group.label}
              {count > 0 && (
                <span className="text-xs bg-gray-100 rounded-full px-1.5">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Token List */}
      <div className="flex-1 overflow-y-auto p-4">
        {groupTokens.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No {activeGroup} tokens defined</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={loadPresets}
                className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
              >
                Load Presets
              </button>
              <button
                onClick={addToken}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Add Token
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div className="flex items-center gap-3 pb-2 mb-2 border-b text-xs font-medium text-gray-500 uppercase">
              <span className="w-8" />
              <span className="w-28">Label</span>
              <span className="w-28">Name</span>
              <span className="flex-1">Value</span>
              <span className="w-8" />
            </div>

            {groupTokens.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                group={activeGroup}
                onUpdate={(updates) => updateToken(token.id, updates)}
                onDelete={() => deleteToken(token.id)}
              />
            ))}

            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={addToken}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4" /> Add Token
              </button>
              {groupTokens.length === 0 && (
                <button
                  onClick={loadPresets}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 ml-4"
                >
                  Load Presets
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CSS Output */}
      {cssVariables && (
        <div className="border-t p-4 bg-gray-50">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">CSS Variables Output</p>
          <pre className="text-xs font-mono text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`:root {\n${cssVariables}\n}`}
          </pre>
        </div>
      )}
    </div>
  );
}

export default DesignTokenEditor;
